import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { Shield, RefreshCw } from 'lucide-react';

interface UserStat {
  uid: string;
  email: string;
  displayName: string;
  videosCount: number;
  replicateTokens: number;
}

export function AdminDashboard() {
  const [stats, setStats] = useState<UserStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      const token = await user.getIdToken();
      // Using the export-service URL since our admin route is hosted there.
      // E.g., https://export-service-XYZ.run.app/admin/stats
      // If we're testing locally, use the local port 8080. We'll use the production URL or local accordingly.
      // We see exportServiceUrl is 'https://export-service-400620783493.us-central1.run.app' in App.tsx
      const exportServiceUrl = import.meta.env.VITE_EXPORT_SERVICE_URL || 'https://export-service-400620783493.us-central1.run.app';

      const response = await fetch(`${exportServiceUrl}/admin/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.error || `Error ${response.status}: Failed to fetch stats`);
      }

      const data = await response.json();
      if (data.success && data.stats) {
        setStats(data.stats);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (e: any) {
      console.error('[AdminDashboard] Fetch error:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return (
    <div className="p-8 max-w-7xl mx-auto h-full flex flex-col">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Shield className="text-blue-500 w-8 h-8" />
          Dashboard Administrateur
        </h1>
        <button
          onClick={fetchStats}
          disabled={loading}
          className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Rafraîchir
        </button>
      </div>

      <a
        href="/alpha/admin"
        target="_blank"
        rel="noopener noreferrer"
        className="mb-6 block rounded-xl border border-blue-500/30 bg-blue-500/10 p-4 text-blue-300 hover:bg-blue-500/20 focus-visible:outline focus-visible:outline-2"
      >
        <span className="block font-semibold">Ouvrir le suivi Alpha →</span>
        <span className="text-sm">Projets récents, vidéos importées, exports et erreurs par utilisateur.</span>
      </a>

      {error ? (
        <div className="bg-red-500/20 text-red-400 p-4 rounded-lg flex items-center gap-3 border border-red-500/50">
          <Shield className="w-5 h-5 flex-shrink-0" />
          <span>Erreur : {error}</span>
        </div>
      ) : (
        <div className="bg-slate-800 rounded-xl overflow-hidden border border-slate-700 flex-1 flex flex-col">
          <div className="overflow-x-auto flex-1">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-900 border-b border-slate-700">
                  <th className="p-4 font-semibold text-slate-300">Utilisateur</th>
                  <th className="p-4 font-semibold text-slate-300">Email</th>
                  <th className="p-4 font-semibold text-slate-300">Projets créés</th>
                  <th className="p-4 font-semibold text-slate-300">Tokens Replicate</th>
                </tr>
              </thead>
              <tbody>
                {loading && stats.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-slate-400">
                      Chargement des statistiques...
                    </td>
                  </tr>
                ) : stats.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-slate-400">
                      Aucun utilisateur trouvé.
                    </td>
                  </tr>
                ) : (
                  stats.map((stat) => (
                    <tr key={stat.uid} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                      <td className="p-4 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold text-sm">
                          {(stat.displayName || stat.email || '?')[0].toUpperCase()}
                        </div>
                        <span className="font-medium">{stat.displayName}</span>
                      </td>
                      <td className="p-4 text-slate-300">{stat.email}</td>
                      <td className="p-4">
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-md font-semibold font-mono">
                          {stat.videosCount}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-md font-semibold font-mono">
                          {stat.replicateTokens}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
